import {
  ElementType,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  memo,
  useEffect,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Box, Flex, Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import DashboardIcon from 'components/Icons/DashboardIcon';
import DashboardIconActive from 'components/Icons/DashboardIconActive';
import DimensionsIcon from 'components/Icons/DimensionsIcon';
import DimensionsIconActive from 'components/Icons/DimensionsIconActive';
import ImportDataIcon from 'components/Icons/ImportDataIcon';
import ImportDataIconActive from 'components/Icons/ImportDataIconActive';
import { FetchFormulaList, getBaseScenario, getFormulaList } from 'redux/PlannerModeSlice';
import { handleBuilderSelectedDimension } from 'redux/BuilderModeSlice';
import { selectedFileId } from 'redux/DataImportSlice';
import {
  getDashboard,
  getAllInstruments,
  getSelectedDashboardScenario,
  getSelectedPage,
  setSelectedPage,
  getModelDashboards,
  getSelectedDashboardId,
  loadDashboardById,
  createModelDashboardPack,
  duplicateModelDashboardPack,
  deleteModelDashboardPack,
  renameModelDashboardPack,
  initializeModelDashboards,
  type ModelDashboardListItem,
} from 'redux/DashboardSliceV2';
import { getBaseScenario as getScenarioSliceBaseScenario } from 'redux/ScenarioSlice';
import { getBlocks } from 'redux/ModelsSlice';
import { Flags } from 'models/Global.interface';
import useDashboardData from 'pages/ModelOverviewPage/Dashboard-v2/hooks/useDashboardData';
import { accent, primaryAction, radius } from 'styles/revampTokens';
import { isUiRevampEnabled } from 'utils/uiRevampFlag';
import { isBlockDimDropdownEnabled, isDataImportDropdownEnabled } from 'utils/navDropdownFlag';
import { buildModelDashboardUrl, buildModelDashboardsTabUrl } from 'utils/dashboardModelRoutes';
import bloxBiService from 'services/bloxBiService';

import { getUrlValueFromTab, TAB_KEYS } from '../constants';
import { TabType } from '../types';

import BlockNavigationDropdown, {
  BlockNavigationClickOptions,
  BlockNavigationDropdownItem,
} from './BlockNavigationDropdown';
import GenericNavigationDropdown, { GenericDropdownVariant } from './GenericNavigationDropdown';
import DashboardNavigationDropdown, { DashboardNavPack } from './DashboardNavigationDropdown';
import BlocksIconActive from './icons/BlocksIconActive';
import BlocksIcon from './icons/BlocksIcon';
import NavigationArrowDownIcon from './NavigationArrowDownIcon';

interface NavigationBarProperties {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  flags?: Flags;
}

interface NavigationIconProperties {
  width?: number;
  height?: number;
}

interface NavigationTab {
  key: TabType;
  label: string;
  icon: (properties: NavigationIconProperties) => ReactElement;
  activeIcon: (properties: NavigationIconProperties) => ReactElement;
}

type NavigationDropdownSection = BlockNavigationDropdownItem;

const ACTIVE_TAB_COLOR = accent.solid;
const DROPDOWN_LAYER_Z_INDEX = 4000;

const tabs: NavigationTab[] = [
  {
    key: TAB_KEYS.DASHBOARD,
    label: 'Dashboards',
    icon: DashboardIcon,
    activeIcon: DashboardIconActive,
  },
  {
    key: TAB_KEYS.BLOCKS,
    label: 'Blocks',
    icon: BlocksIcon,
    activeIcon: BlocksIconActive,
  },
  {
    key: TAB_KEYS.DIMENSIONS,
    label: 'Dimensions',
    icon: DimensionsIcon,
    activeIcon: DimensionsIconActive,
  },
  {
    key: TAB_KEYS.IMPORT,
    label: 'Data',
    icon: ImportDataIcon,
    activeIcon: ImportDataIconActive,
  },
];

const NavigationFlex = Flex as ElementType;
const NavigationBox = Box as ElementType;

const NavigationPopover = Popover as ElementType;
const NavigationPopoverTrigger = PopoverTrigger as ElementType;
const NavigationPopoverContent = PopoverContent as ElementType;
const NavigationPortal = Portal as ElementType;
const MotionBox = motion(Box);

const toDropdownSections = (
  fallbackIdPrefix: string,
  items: any[],
  getTitle: (item: any) => string | undefined,
): NavigationDropdownSection[] => {
  return (items || [])
    .map((item: any, index: number) => ({
      id: item?.id ?? `${fallbackIdPrefix}-${index}`,
      title: getTitle(item) || '',
      source: item,
    }))
    .filter((section) => Boolean(section.title));
};

const sortDashboardPages = (pages: any[] | undefined) =>
  [...(pages || [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

const buildDashboardNavPacks = (
  modelDashboards: ModelDashboardListItem[],
  activeDashboardId: string | null,
  dashboardPages: any[] | undefined,
): DashboardNavPack[] => {
  const sortedPages = sortDashboardPages(dashboardPages);
  const pageSections = toDropdownSections('page', sortedPages, (page) => page?.name);
  return (modelDashboards || []).map((row) => ({
    id: row.id,
    title: row.name || 'Dashboard',
    description: row.description ?? undefined,
    page_count: row.page_count,
    updated_at: row.updated_at,
    pages: row.id === activeDashboardId ? pageSections : [],
    source: row as Record<string, unknown>,
  }));
};

const getGenericVariant = (tabKey: TabType): GenericDropdownVariant | null => {
  if (tabKey === TAB_KEYS.DASHBOARD) return 'dashboard';
  if (tabKey === TAB_KEYS.DIMENSIONS) return 'dimensions';
  if (tabKey === TAB_KEYS.IMPORT) return 'data';
  return null;
};

const getModelTabUrl = (modelId: string | number, tabKey: TabType) =>
  `/model/${modelId}/${getUrlValueFromTab(tabKey)}`;

const saveSelectedDashboardPage = (
  modelId: string | number,
  pageId: string | number | undefined,
) => {
  try {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[Number(modelId)] = {
      ...data[Number(modelId)],
      selectedPageId: pageId,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  } catch {
    // Ignore localStorage errors (private mode, quota, etc.)
  }
};

const buildDropdownSections = (
  tabKey: TabType,
  formulaList: any,
  dashboardPages: any[] | undefined,
  uiRevamp: boolean,
  modelBlocks?: any[],
): NavigationDropdownSection[] => {
  switch (tabKey) {
    case TAB_KEYS.BLOCKS:
      return toDropdownSections(
        'block',
        formulaList?.blocks?.length ? formulaList.blocks : modelBlocks,
        (block) => block?.name,
      );
    case TAB_KEYS.DIMENSIONS:
      return toDropdownSections(
        'dimension',
        formulaList?.dimensions || formulaList?.dimension,
        (dimension) => dimension?.name,
      );
    case TAB_KEYS.IMPORT:
      return toDropdownSections(
        'data-source',
        formulaList?.data_source || formulaList?.data_sources,
        (dataSource) => dataSource?.file_name,
      );
    case TAB_KEYS.DASHBOARD:
      if (!uiRevamp) {
        return [];
      }
      return toDropdownSections('page', sortDashboardPages(dashboardPages), (page) => page?.name);
    default:
      return [];
  }
};

const isDropdownEnabledForTab = (tabKey: TabType, flags?: Flags): boolean => {
  if (tabKey === TAB_KEYS.DASHBOARD) {
    return isUiRevampEnabled(flags);
  }
  if (tabKey === TAB_KEYS.BLOCKS || tabKey === TAB_KEYS.DIMENSIONS) {
    return isBlockDimDropdownEnabled(flags as { blockDimDropdown?: boolean } | undefined);
  }
  if (tabKey === TAB_KEYS.IMPORT) {
    return isDataImportDropdownEnabled(flags as { dataImportDropdown?: boolean } | undefined);
  }
  return false;
};

const buildDropdownSectionUrl = (
  tabKey: TabType,
  section: NavigationDropdownSection,
  modelId?: string,
  activeDashboardId?: string | null,
): string | null => {
  if (!modelId) {
    return null;
  }

  const encodedId = encodeURIComponent(String(section.id));
  if (tabKey === TAB_KEYS.BLOCKS) {
    return `/model/${modelId}/block/${encodedId}/plan`;
  }
  if (tabKey === TAB_KEYS.DIMENSIONS) {
    return `${getModelTabUrl(modelId, TAB_KEYS.DIMENSIONS)}?dimensionId=${encodedId}`;
  }
  if (tabKey === TAB_KEYS.IMPORT) {
    return `${getModelTabUrl(
      modelId,
      TAB_KEYS.IMPORT,
    )}?fileId=${encodedId}&fileName=${encodeURIComponent(section.title)}`;
  }
  if (tabKey === TAB_KEYS.DASHBOARD) {
    if (!activeDashboardId) {
      return buildModelDashboardsTabUrl(modelId);
    }
    return buildModelDashboardUrl(modelId, activeDashboardId, section.id);
  }
  return null;
};

const openUrlInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
};

const NavigationBar = memo(
  ({ activeTab, onTabChange, flags }: NavigationBarProperties): ReactElement => {
    const { modelId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const [openDropdownKey, setOpenDropdownKey] = useState<TabType | null>(null);
    const formulaList = useSelector(getFormulaList);
    const model = useSelector(getBlocks);
    const dashboard = useSelector(getDashboard);
    const modelDashboards = useSelector(getModelDashboards);
    const selectedDashboardId = useSelector(getSelectedDashboardId);
    const allInstruments = useSelector(getAllInstruments);
    const selectedPage = useSelector(getSelectedPage);
    const plannerScenario = useSelector(getBaseScenario);
    const dashboardScenario = useSelector(getSelectedDashboardScenario);
    const scenarioSliceScenario = useSelector(getScenarioSliceBaseScenario);
    const selectedScenarioId =
      dashboardScenario?.id ?? plannerScenario?.id ?? scenarioSliceScenario?.id;
    const uiRevamp = isUiRevampEnabled(flags);
    const blockDimDropdownOn = isBlockDimDropdownEnabled(
      flags as { blockDimDropdown?: boolean } | undefined,
    );
    const dataImportDropdownOn = isDataImportDropdownEnabled(
      flags as { dataImportDropdown?: boolean } | undefined,
    );
    const hasEnabledDropdown = Boolean(blockDimDropdownOn || dataImportDropdownOn || uiRevamp);
    useDashboardData({ enable: uiRevamp });
    useEffect(() => {
      if (modelId && hasEnabledDropdown && selectedScenarioId) {
        dispatch(FetchFormulaList({ modelId: Number(modelId), scenarioId: selectedScenarioId }));
      }
    }, [dispatch, hasEnabledDropdown, modelId, selectedScenarioId]);

    useEffect(() => {
      const searchParameters = new URLSearchParams(location.search);
      const dimensionId = searchParameters.get('dimensionId');
      const fileId = searchParameters.get('fileId');
      const pageId = searchParameters.get('pageId');

      if (pageId && uiRevamp && modelId) {
        const matchedPage =
          (dashboard?.pages || []).find((p: any) => String(p?.id) === pageId) ?? null;
        if (matchedPage) {
          saveSelectedDashboardPage(modelId, matchedPage.id);
          dispatch(setSelectedPage(matchedPage));
        }
      }

      if (dimensionId && blockDimDropdownOn) {
        const selectedDimension =
          (formulaList?.dimensions || formulaList?.dimension || []).find(
            (dimension: any) => String(dimension?.id) === dimensionId,
          ) ?? null;
        if (selectedDimension) {
          dispatch(handleBuilderSelectedDimension(selectedDimension));
        }
      }

      if (fileId && dataImportDropdownOn) {
        const numericFileId = Number(fileId);
        dispatch(
          selectedFileId({
            id: Number.isFinite(numericFileId) ? numericFileId : fileId,
            name: searchParameters.get('fileName') ?? '',
            deatsilsPage: true,
          }),
        );
      }
    }, [
      dashboard?.pages,
      dispatch,
      blockDimDropdownOn,
      dataImportDropdownOn,
      formulaList,
      location.search,
      modelId,
      uiRevamp,
    ]);

    const navigateNow = (url: string) => {
      flushSync(() => {
        navigate(url);
      });
    };

    const activeDashboardIdForUrls =
      selectedDashboardId ?? (dashboard?.id ? String(dashboard.id) : null);

    const handleDropdownSectionClick = (
      tabKey: TabType,
      section: NavigationDropdownSection,
      options?: BlockNavigationClickOptions,
    ) => {
      const url = buildDropdownSectionUrl(
        tabKey,
        section,
        modelId,
        tabKey === TAB_KEYS.DASHBOARD ? activeDashboardIdForUrls : undefined,
      );

      if (options?.openInNewTab) {
        if (tabKey === TAB_KEYS.DASHBOARD && modelId) {
          saveSelectedDashboardPage(modelId, section.id);
        }
        if (url) {
          openUrlInNewTab(url);
        }
        return;
      }

      if (tabKey === TAB_KEYS.DASHBOARD && modelId && uiRevamp) {
        const page = section.source;
        saveSelectedDashboardPage(modelId, page?.id);
        dispatch(setSelectedPage(page));
        if (url) {
          navigateNow(url);
        } else {
          onTabChange(TAB_KEYS.DASHBOARD);
        }
        return;
      }

      if (tabKey === TAB_KEYS.BLOCKS && url) {
        navigateNow(url);
        return;
      }

      if (tabKey === TAB_KEYS.DIMENSIONS && modelId) {
        dispatch(handleBuilderSelectedDimension(section.source));
        if (url) {
          navigateNow(url);
        } else {
          onTabChange(TAB_KEYS.DIMENSIONS);
        }
        return;
      }

      if (tabKey === TAB_KEYS.IMPORT) {
        dispatch(
          selectedFileId({
            id: section.id,
            name: section.title,
            deatsilsPage: true,
          }),
        );
        if (url) {
          navigateNow(url);
        } else {
          onTabChange(TAB_KEYS.IMPORT);
        }
      }
    };

    const dashboardNavPacks = buildDashboardNavPacks(
      modelDashboards,
      selectedDashboardId ?? (dashboard?.id ? String(dashboard.id) : null),
      dashboard?.pages,
    );

    const fetchPackPagesForNav = async (dashboardId: string) => {
      const response = await bloxBiService.getDashboardById(dashboardId);
      return toDropdownSections(
        'page',
        sortDashboardPages(response.data?.pages),
        (page) => page?.name,
      );
    };

    const handleDashboardOpen = async (dashboardId: string) => {
      if (!modelId) return;
      const result = await dispatch(loadDashboardById({ dashboardId, modelId }));
      if (result.meta?.requestStatus !== 'fulfilled') {
        return;
      }
      const pages = sortDashboardPages(result.payload?.pages);
      const firstPage = pages[0];
      if (firstPage) {
        saveSelectedDashboardPage(modelId, firstPage.id);
        dispatch(setSelectedPage(firstPage));
        navigateNow(buildModelDashboardUrl(modelId, dashboardId, firstPage.id));
        return;
      }
      navigateNow(buildModelDashboardUrl(modelId, dashboardId));
    };

    const handleCreateDashboard = () => {
      if (!model) return;
      dispatch(createModelDashboardPack({ model }));
    };

    const handleDuplicateDashboard = (dashboardId: string) => {
      dispatch(duplicateModelDashboardPack({ dashboardId }));
    };

    const handleDeleteDashboard = (dashboardId: string) => {
      if (!modelId) return;
      dispatch(deleteModelDashboardPack({ dashboardId, modelId })).then((result: any) => {
        if (result.meta?.requestStatus === 'fulfilled') {
          dispatch(initializeModelDashboards({ model }));
        }
      });
    };

    const handleRenameDashboard = (dashboardId: string, name: string) => {
      const pack = modelDashboards.find((row) => row.id === dashboardId);
      const source =
        dashboardId === String(dashboard?.id)
          ? { ...dashboard, name }
          : {
              id: dashboardId,
              name,
              model_id: model?.id,
              description: pack?.description ?? '',
              pages: [],
              start_time: dashboard?.start_time,
              end_time: dashboard?.end_time,
            };
      dispatch(renameModelDashboardPack({ dashboardId, name, dashboard: source }));
    };

    const handleViewAllBlocksClick = (options?: BlockNavigationClickOptions) => {
      if (!modelId) {
        return;
      }

      const url = getModelTabUrl(modelId, TAB_KEYS.BLOCKS);
      if (options?.openInNewTab) {
        openUrlInNewTab(url);
        return;
      }

      flushSync(() => {
        onTabChange(TAB_KEYS.BLOCKS);
      });
    };

    const renderedTabs = tabs.map(
      ({ key, label, icon: IconComponent, activeIcon: ActiveIconComponent }) => {
        const isActive = activeTab === key;
        const IconToRender = isActive ? ActiveIconComponent : IconComponent;
        const isDropdownEnabled = isDropdownEnabledForTab(key, flags);
        const dropdownSections = isDropdownEnabled
          ? buildDropdownSections(key, formulaList, dashboard?.pages, uiRevamp, model?.blocks).map(
              (section) => ({
                ...section,
                href: buildDropdownSectionUrl(
                  key,
                  section,
                  modelId,
                  key === TAB_KEYS.DASHBOARD ? activeDashboardIdForUrls : undefined,
                ),
              }),
            )
          : [];
        const showDropdown =
          isDropdownEnabled &&
          (key === TAB_KEYS.BLOCKS || key === TAB_KEYS.DASHBOARD || dropdownSections.length > 0);

        const tabButton = (
          <NavigationFlex
            alignItems='center'
            justifyContent='center'
            gap='8px'
            // h='35px'
            px='16px'
            py='6px'
            borderRadius={radius.control}
            cursor='pointer'
            flexShrink={0}
            position='relative'
            bg='transparent'
            color={primaryAction.bg}
            transition='background 0.15s ease, transform 0.1s ease'
            _hover={{ bg: isActive ? 'transparent' : 'gray.100' }}
            _active={{ bg: isActive ? 'transparent' : 'gray.200', transform: 'scale(0.97)' }}
            onClick={() => {
              setOpenDropdownKey(null);
              onTabChange(key);
            }}
          >
            {isActive && (
              <MotionBox
                layoutId='activeNavTabIndicator'
                position='absolute'
                inset={0}
                bg={ACTIVE_TAB_COLOR}
                borderRadius={radius.control}
                zIndex={0}
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
            <Box
              position='relative'
              zIndex={1}
              display='flex'
              alignItems='center'
              justifyContent='center'
              w='16px'
              h='16px'
            >
              <IconToRender width={16} height={16} />
            </Box>

            <Box
              as='span'
              position='relative'
              zIndex={1}
              color={isActive ? 'white' : primaryAction.bg}
              fontSize='12px'
              fontWeight='500'
              lineHeight='1'
              whiteSpace='nowrap'
              textAlign='center'
            >
              {label}
            </Box>
            {isDropdownEnabled && (
              <NavigationBox
                position='relative'
                zIndex={1}
                display='flex'
                alignItems='center'
                justifyContent='center'
                w='10px'
                h='6px'
              >
                <NavigationArrowDownIcon isActive={isActive} />
              </NavigationBox>
            )}
          </NavigationFlex>
        );

        if (!showDropdown) {
          return <NavigationBox key={key}>{tabButton}</NavigationBox>;
        }

        return (
          <NavigationPopover
            key={key}
            isOpen={openDropdownKey === key}
            placement='bottom'
            gutter={8}
            isLazy
            autoFocus={false}
            returnFocusOnClose={false}
            onClose={() => setOpenDropdownKey(null)}
          >
            <NavigationPopoverTrigger>
              <Box
                onMouseEnter={() => setOpenDropdownKey(key)}
                onMouseLeave={() => setOpenDropdownKey(null)}
                display='inline-flex'
              >
                {tabButton}
              </Box>
            </NavigationPopoverTrigger>
            <NavigationPortal>
              <NavigationPopoverContent
                zIndex={DROPDOWN_LAYER_Z_INDEX}
                w='auto'
                minW='unset'
                maxW={
                  key === TAB_KEYS.BLOCKS
                    ? 'min(760px, 96vw)'
                    : key === TAB_KEYS.DASHBOARD
                    ? 'min(720px, 96vw)'
                    : 'min(540px, 96vw)'
                }
                border='none'
                bg='transparent'
                boxShadow='none'
                p={0}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                rootProps={{ style: { zIndex: DROPDOWN_LAYER_Z_INDEX } }}
                onMouseEnter={() => setOpenDropdownKey(key)}
                onMouseLeave={() => setOpenDropdownKey(null)}
                onClick={(event: ReactMouseEvent) => event.stopPropagation()}
                onMouseDown={(event: ReactMouseEvent) => event.stopPropagation()}
              >
                {key === TAB_KEYS.BLOCKS ? (
                  <BlockNavigationDropdown
                    blocks={dropdownSections}
                    onBlockClick={(section, options) =>
                      handleDropdownSectionClick(key, section, options)
                    }
                    onViewAllBlocksClick={handleViewAllBlocksClick}
                    viewAllHref={modelId ? getModelTabUrl(modelId, TAB_KEYS.BLOCKS) : null}
                  />
                ) : key === TAB_KEYS.DASHBOARD && openDropdownKey === TAB_KEYS.DASHBOARD ? (
                  <DashboardNavigationDropdown
                    dashboards={dashboardNavPacks}
                    activeDashboardId={selectedDashboardId ?? dashboard?.id}
                    currentPageId={selectedPage?.id}
                    allInstruments={allInstruments}
                    onDashboardOpen={handleDashboardOpen}
                    onFetchPackPages={fetchPackPagesForNav}
                    onPageClick={(section, options) =>
                      handleDropdownSectionClick(key, section, options)
                    }
                    onCreateDashboard={handleCreateDashboard}
                    onDuplicateDashboard={handleDuplicateDashboard}
                    onDeleteDashboard={handleDeleteDashboard}
                    onRenameDashboard={handleRenameDashboard}
                    canDeleteDashboard={modelDashboards.length > 1}
                  />
                ) : key === TAB_KEYS.DASHBOARD ? null : (
                  (() => {
                    const variant = getGenericVariant(key);
                    if (!variant) return null;
                    return (
                      <GenericNavigationDropdown
                        items={dropdownSections}
                        variant={variant}
                        onItemClick={(section, options) =>
                          handleDropdownSectionClick(key, section, options)
                        }
                      />
                    );
                  })()
                )}
              </NavigationPopoverContent>
            </NavigationPortal>
          </NavigationPopover>
        );
      },
    );

    return (
      <NavigationFlex
        alignItems='center'
        width='fit-content'
        bg='white'
        borderRadius={radius.control}
        flexWrap='nowrap'
        border='1px solid #D1D6DD'
        outline='1px solid #D1D6DD'
        outlineOffset='-1px'
        gap='1px'
        overflow='visible'
        p='0'
      >
        {renderedTabs}
      </NavigationFlex>
    );
  },
);

NavigationBar.displayName = 'NavigationBar';
export default withLDConsumer()(NavigationBar);
